import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Grid,
  Skeleton,
  Button,
} from "@mui/material";
import React, { useState } from "react";
import Grid2 from "@mui/material/Grid2";
import { useDispatch, useSelector } from "react-redux";
import ExpandLessTwoToneIcon from "@mui/icons-material/ExpandLessTwoTone";
import ExpandMoreTwoToneIcon from "@mui/icons-material/ExpandMoreTwoTone";
import { format_Number } from "../../utils/utils";
import CardSection from "./CardSectionSalon";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  setQuantities,
  setselectedUpsaleProducts,
} from "../../store/slice/Salon.slice";

const UpsaleProduct = ({
  steps,
  activeStep,
  handleBack,
  handleNextFunc,
  price,
  errors,
  isLoading,
}) => {
  const IMG_URL = process.env.REACT_APP_API_LOCAL_IMG_URL;
  const {
    upsaleProducts,
    selectedUpsaleProducts,
    quantities,
    upsaleLoader,
    languageWords,
  } = useSelector((store) => store.salon);
  const { css } = useSelector((store) => store?.widgetSettings);
  const Css = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Extra products"
  )?.fields;

  const theme = useTheme();
  const dispatch = useDispatch();
  const [isShowMore, setIsShowMore] = useState(false);
  const [imgErrors, setImgErrors] = useState({});

  const productsToDisplay =
    upsaleProducts?.length > 0
      ? isShowMore
        ? upsaleProducts
        : upsaleProducts?.slice(0, 4)
      : [];

  const isProductSelected = (item) =>
    selectedUpsaleProducts?.some(
      (product) => product.idproduct === item.idproduct
    );

  /** Deselect helper — removes from selected + resets quantity */
  const deselect = (item) => {
    dispatch(
      setselectedUpsaleProducts(
        selectedUpsaleProducts.filter(
          (product) => product.idproduct !== item.idproduct
        )
      )
    );
    dispatch(setQuantities({ ...quantities, [item.idproduct]: 0 }));
  };

  /** Select helper — adds to selected with quantity 1 (or current if already set) */
  const select = (item) => {
    const qty = quantities[item.idproduct] > 0 ? quantities[item.idproduct] : 1;
    dispatch(
      setselectedUpsaleProducts([
        ...selectedUpsaleProducts,
        { ...item, quantity: qty },
      ])
    );
    dispatch(setQuantities({ ...quantities, [item.idproduct]: qty }));
  };

  /** Toggle selection when clicking anywhere on the card */
  const handleCardClick = (item) => {
    if (isProductSelected(item)) {
      deselect(item);
    } else {
      select(item);
    }
  };

  /** + / − buttons */
  const handleQuantityChange = (e, item, increment) => {
    e.stopPropagation(); // don't bubble to card click
    const current = quantities[item.idproduct] || 1;

    if (!increment && current <= 1) {
      // At quantity 1 pressing − → deselect
      deselect(item);
      return;
    }

    const newQty = Math.max(current + (increment ? 1 : -1), 0);
    dispatch(setQuantities({ ...quantities, [item.idproduct]: newQty }));

    const updated = selectedUpsaleProducts.map((p) =>
      p.idproduct === item.idproduct ? { ...p, quantity: newQty } : p
    );
    if (!updated.some((p) => p.idproduct === item.idproduct)) {
      updated.push({ ...item, quantity: newQty });
    }
    dispatch(setselectedUpsaleProducts(updated));
  };

  /** Add/Remove button inside the card (separate from card click) */
  const handleAddBtnClick = (e, item) => {
    e.stopPropagation();
    if (isProductSelected(item)) {
      deselect(item);
    } else {
      select(item);
    }
  };

  if (upsaleLoader) {
    return (
      <Box>
        {Array.from({ length: 3 }).map((_, index) => (
          <Box
            key={index}
            sx={{
              border: "1px solid #EBEBEB",
              p: 3,
              borderRadius: "10px",
              backgroundColor: "#fff",
              mb: 2,
              boxShadow: "rgba(0, 0, 0, 0.1) 2px 2px 8px",
              width: "100%",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={2}>
                <Skeleton variant="rectangular" width="100%" height={80} />
              </Grid>
              <Grid item xs={12} sm={8} md={10}>
                <Skeleton variant="text" width="80%" height={30} />
                <Skeleton variant="text" width="60%" height={25} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 2 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width={40} />
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="circular" width={50} height={50} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <Grid2 container spacing={2}>
        <Grid2 size={{ md: 9, sm: 12, xs: 12 }}>
          {productsToDisplay?.map((item, index) => {
            const selected = isProductSelected(item);
            const hasImage = item?.imageFileName && !imgErrors[item.idproduct];
            const qty = quantities[item.idproduct] || 1;

            return (
              <Box
                key={index}
                onClick={() => handleCardClick(item)}
                sx={{
                  border: `1px solid ${theme?.product?.cardBorder ?? "#EBEBEB"}`,
                  backgroundColor: selected
                    ? Css?.salonupsaleSelectItemBgClr
                    : Css?.salonupsaleItemBgClr,
                  p: 2,
                  borderRadius: "10px",
                  mb: 2,
                  boxShadow: "rgba(0, 0, 0, 0.1) 2px 2px 8px",
                  width: "100%",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  userSelect: "none",
                  "&:hover": {
                    boxShadow: "rgba(0, 0, 0, 0.18) 2px 4px 14px",
                  },
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  {/* ── Image ── */}
                  {hasImage && (
                    <Grid item xs={12} sm={4} md={2}>
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={`${IMG_URL}upload/product${item?.imageFileName.replace(/\\/g, "/")}`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onLoad={() =>
                            setImgErrors((prev) => ({ ...prev, [item.idproduct]: false }))
                          }
                          onError={() =>
                            setImgErrors((prev) => ({ ...prev, [item.idproduct]: true }))
                          }
                          alt={item?.productName}
                        />
                      </Box>
                    </Grid>
                  )}

                  {/* ── Content ── */}
                  <Grid item xs={12} sm={hasImage ? 8 : 12} md={hasImage ? 10 : 12}>
                    <Box sx={{ width: "100%" }}>
                      {/* Title row + Add button */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: "bold",
                              wordBreak: "break-word",
                              color: selected
                                ? Css?.salonupsaleSelectHeadingClr
                                : Css?.salonUpsaleHeadingClr,
                            }}
                          >
                            {item?.productName}
                          </Typography>
                          {item?.productinformation && (
                            <Typography
                              sx={{
                                color: selected
                                  ? Css?.salonupsaleSelectInfoClr
                                  : Css?.salonupsaleInfoClr,
                                mt: 0.5,
                                fontSize: "13px",
                              }}
                            >
                              {item?.productinformation}
                            </Typography>
                          )}
                        </Box>

                        {/* Add / Remove button */}
                        <Button
                          variant="outlined"
                          onClick={(e) => handleAddBtnClick(e, item)}
                          sx={{
                            position: "relative",
                            borderRadius: "12px",
                            padding: "8px 16px",
                            minWidth: "110px",
                            flexShrink: 0,
                            borderColor: Css?.salonupsaleBtnBorder,
                            backgroundColor: selected
                              ? Css?.salonupsaleSelectItemBgClr
                              : "transparent",
                            textTransform: "none",
                            display: "flex",
                            flexDirection: "column",
                            lineHeight: 1.2,
                            "&:hover": {
                              borderColor: theme?.product?.addBtnBorderHover ?? "#bdbdbd",
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: selected ? "#fff" : Css?.salonupsaleBtnTxt,
                            }}
                          >
                            {selected ? languageWords?.Remove ?? "Remove" : languageWords?.Add}
                          </Typography>
                          {item?.price > 0 && (
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: selected ? "#fff" : Css?.salonupsaleBtnTxt,
                              }}
                            >
                              €{format_Number(parseFloat(item?.price).toFixed(2))}
                            </Typography>
                          )}
                          {selected && (
                            <DeleteOutlineIcon
                              sx={{
                                position: "absolute",
                                bottom: 6,
                                right: 6,
                                fontSize: 18,
                                color: "#fff",
                                opacity: 0.9,
                                "&:hover": { opacity: 1 },
                              }}
                            />
                          )}
                        </Button>
                      </Box>

                      {/* Quantity pill */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            px: 1,
                            py: 0.5,
                            borderRadius: "999px",
                            backgroundColor: selected
                              ? theme?.product?.quantityPillActiveBg ?? "rgba(255,255,255,0.15)"
                              : theme?.product?.quantityPillBg ?? "rgba(0,0,0,0.04)",
                            backdropFilter: "blur(6px)",
                          }}
                          onClick={(e) => e.stopPropagation()} // pill clicks don't toggle card
                        >
                          <Button
                            onClick={(e) => handleQuantityChange(e, item, false)}
                            disableRipple
                            sx={{
                              minWidth: 36,
                              height: 36,
                              borderRadius: "50%",
                              fontSize: "20px",
                              fontWeight: 600,
                              color: selected
                                ? Css?.salonupsaleHighLightPillTxt
                                : Css?.salonupsalePillTxt,
                              backgroundColor: "transparent",
                              transition: "all 0.25s ease",
                              "&:active": { transform: "scale(0.95)" },
                            }}
                          >
                            −
                          </Button>

                          <Typography
                            sx={{
                              minWidth: 28,
                              textAlign: "center",
                              fontSize: "14px",
                              fontWeight: 600,
                              color: selected
                                ? Css?.salonupsaleHighLightPillTxt
                                : Css?.salonupsalePillTxt,
                              transition: "color 0.3s ease",
                            }}
                          >
                            {qty}
                          </Typography>

                          <Button
                            onClick={(e) => handleQuantityChange(e, item, true)}
                            disableRipple
                            sx={{
                              minWidth: 36,
                              height: 36,
                              borderRadius: "50%",
                              fontSize: "20px",
                              fontWeight: 600,
                              color: selected
                                ? Css?.salonupsaleHighLightPillTxt
                                : Css?.salonupsalePillTxt,
                              backgroundColor: "transparent",
                              transition: "all 0.25s ease",
                              "&:active": { transform: "scale(0.95)" },
                            }}
                          >
                            +
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            );
          })}

          {upsaleProducts?.data?.length > 4 && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <IconButton
                sx={{
                  backgroundColor: theme?.product?.addBtnBg,
                  fontSize: "14px",
                  color: theme?.product?.addBtnTextColor,
                  "&:hover": {
                    backgroundColor: theme?.product?.addBtnBgHover,
                    color: theme?.product?.addBtnTextHover,
                  },
                }}
                onClick={() => setIsShowMore(!isShowMore)}
              >
                {!isShowMore ? <ExpandMoreTwoToneIcon /> : <ExpandLessTwoToneIcon />}
              </IconButton>
            </Box>
          )}
        </Grid2>

        <Grid2 size={{ md: 3, sm: 12, xs: 12 }}>
          <CardSection
            steps={steps}
            activeStep={activeStep}
            handleBack={handleBack}
            handleNextFunc={handleNextFunc}
            price={price}
            errors={errors}
            isLoading={isLoading}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default UpsaleProduct;
